import React from "react"

import { PersonGroupApi } from "../services/PersonGroupApi"

import PersonGroup from "../types/PersonGroup"

import {MessageInterface} from "../components/common/Message";
import LockingSystemPermission from "@customTypes/LockingSystemPermission";
import {PersonGroupLockingSystemPermissionAdd} from "@customTypes/PersonGroupLockingSystemPermission";
import PersonGroupLockingSystemPermissionApi from "../services/PersonGroupLockingSystemPermissionApi";

export type PersonGroupContextType = {
    addItem:      (personGroup: PersonGroup) => Promise<PersonGroup | void> | void
    deleteItem:   (personGroup: PersonGroup) => Promise<any> | void
    editItem:     (personGroup: PersonGroup) => Promise<PersonGroup | void> | void
    fetchItem:    (id: number, include?: string) => void;
    fetchItems:   (search?: string, force?: boolean) => void
    items:        PersonGroup[] | undefined;
    loadingItems: boolean;

    inProgress:   boolean;

    message: MessageInterface | undefined
    closeMessage: () => void

    addLockingSystemPermissions:   (item: PersonGroup, lockingSystemPermissionIds: number[]) => Promise<void> | void
    deleteLockingSystemPermission: (item: PersonGroup, lockingSystemPermission: LockingSystemPermission) => void
    deleteAllPermissions:          (item: PersonGroup) => void;
}

const initialState = {
    addItem:      () => {},
    deleteItem:   () => {},
    editItem:     () => {},
    fetchItem:    () => {},
    fetchItems:   () => {},
    items:        undefined,
    loadingItems: true,

    inProgress:   false,

    message:      undefined,
    closeMessage: () => {},

    addLockingSystemPermissions:   () => {},
    deleteLockingSystemPermission: () => {},
    deleteAllPermissions:          () => {},
}

export const PersonGroupContext = React.createContext<PersonGroupContextType>(initialState);

interface PersonGroupProviderProps {
    children: React.ReactNode
}

const PersonGroupProvider: React.FC<PersonGroupProviderProps> = ( { children }) => {

    const [
        items, setItems
    ] = React.useState<PersonGroup[] | undefined>(initialState.items)
    const [
        loadingItems, setLoadingItems
    ] = React.useState<boolean>(initialState.loadingItems)
    const [
        inProgress, setInProgress
    ] = React.useState<boolean>(initialState.inProgress)
    const [
        message, setMessage
    ] = React.useState<MessageInterface | undefined>(initialState.message)
    // const [
    //     selectOptions, setSelectOptions
    // ] = React.useState<SelectOption[] | undefined>(initialState.selectOptions)

    const fetchItem = async (id: number, include?: string): Promise<PersonGroup | void> => {
        setLoadingItems(true)

        const result = await PersonGroupApi.get(id, include)
            .catch((e) => setErrorMessage(e))

        setLoadingItems(false)

        return result
    }

    const fetchItems = async (search?: string, force: boolean = false) => {
        if (items === undefined || force) {
            setLoadingItems(true)

            const result = await PersonGroupApi.getAll(search)
                .catch((e) => setErrorMessage(e))

            if (result) {
                setItems(result)
                setLoadingItems(false)
            }
        }
    }

    // const fetchSelectOptions = async (force: boolean = false): Promise<SelectOption[] | void> => {
    //
    //     if (undefined === selectOptions || force) {
    //         setLoadingItems(true)
    //
    //         const result = await PersonApi.getSelectOptions()
    //             .catch((e) => setErrorMessage(e))
    //
    //         if (result) {
    //             setSelectOptions(result)
    //
    //             setLoadingItems(false)
    //         }
    //     }
    // }

    const addItem = async (newPersonGroup: PersonGroup): Promise<PersonGroup | void> => {
        closeMessage()
        setInProgress(true)

        const result = await PersonGroupApi.add(newPersonGroup, () => {})
            .catch(e => setErrorMessage(e))

        if (items && result) {
            setInProgress(false)
            setItems([result, ...items])
        }

        return result
    }

    const editItem = async (item: PersonGroup): Promise<PersonGroup | void> => {
        closeMessage()
        setInProgress(true)

        const result = await PersonGroupApi.edit(item)
            .catch((e) => setErrorMessage(e))

        if (items && result) {
            const newItems: PersonGroup[] = items.map(oldItem => {
                return (oldItem.id === item.id && result.id) ? {...result} : oldItem
            })

            setInProgress(false)
            setItems(newItems)
        }

        return result
    }

    const deleteItem = async (item: PersonGroup): Promise<any> => {
        closeMessage()
        setInProgress(true)

        const result = await PersonGroupApi.delete(item.id)
            .catch((e) => setErrorMessage(e))

        if (items && result !== undefined) {
            const newItems = [...items]
            newItems.splice(newItems.findIndex(i => i.id === item.id), 1)

            setInProgress(false)
            setItems(newItems)
        }
    }

    const addLockingSystemPermissions = async (
        item: PersonGroup, lockingSystemPermissionIds: number[]
    ): Promise<any | void> => {
        closeMessage()
        setInProgress(true)

        const personGroupLockingSystemPermissions: PersonGroupLockingSystemPermissionAdd = {
            personGroupId: item.id,
            lockingSystemPermissionIds: lockingSystemPermissionIds
        }

        const result = await PersonGroupLockingSystemPermissionApi
            .add(personGroupLockingSystemPermissions)
            .catch(e => {
                setMessage({
                    text: e.response.data.message || '?',
                    type: 'error'
                })

                setInProgress(false)

                return
            })

        if (result) {
            const newItems = items?.map( item => {
                if (item.id === personGroupLockingSystemPermissions.personGroupId) {
                    item.lockingSystemPermissions = result
                }

                return item
            })

            setItems(newItems)
            setInProgress(false)
        }
    }

    const deleteLockingSystemPermission = async (
        item: PersonGroup, lockingSystemPermission: LockingSystemPermission
    ): Promise<void> => {
        closeMessage()
        setInProgress(true)

        await PersonGroupLockingSystemPermissionApi.delete(item.id, lockingSystemPermission.id)
            .catch(e => {
                setMessage({
                    text: e.response.data.message || '?',
                    type: 'error'
                })

                setInProgress(false)

                return
            })

        const newItems = items?.map(i => {
            if (i.id !== item.id) {
                return i;
            } else {
                const newPermissions = [...i.lockingSystemPermissions || []]
                newPermissions.splice(
                    newPermissions.findIndex(i => i.id === lockingSystemPermission.id),
                    1
                )

                return {...i, lockingSystemPermissions: newPermissions}
            }
        })

        setItems(newItems)
        setInProgress(false)
    }

    const deleteAllPermissions = async (item: PersonGroup): Promise<void> => {
        closeMessage()
        setInProgress(true)

        const result = await PersonGroupLockingSystemPermissionApi
            .deleteAll(item.id)
            .catch((e) => {
                setMessage({
                    text: e.response.data.message || '?',
                    type: 'error'
                })

                setInProgress(false)

                return
            })

        const status: boolean = result.data.status === 'success'

        if (status && items !== undefined) {
            const newItems: PersonGroup[] = items.map(i => {
                if (i.id === item.id) {
                    i.lockingSystemPermissions = []
                }

                return i
            })

            setItems(newItems)
            setInProgress(false)
        }

        return
    }

    const closeMessage = () => setMessage(undefined)

    const setErrorMessage = (e: any) => {
        setMessage({
            text: e.response.data.message || '?',
            type: 'error'
        })

        setInProgress(false)
        setLoadingItems(false)
    }

    return (
        <PersonGroupContext.Provider
            value={{

                addItem,
                deleteItem,
                editItem,
                fetchItem,
                fetchItems,

                addLockingSystemPermissions,
                deleteLockingSystemPermission,
                deleteAllPermissions,

                items,
                loadingItems,
                inProgress,

                message,
                closeMessage,

            }}
        >
            {children}
        </PersonGroupContext.Provider>
    );
}

export const usePersonGroups = () => {
    return React.useContext(PersonGroupContext) as PersonGroupContextType
}

export default PersonGroupProvider